import { Card, Form, Group, OnSubmissionStateChange, Text, useMelioForm } from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import { pick } from 'lodash';
import type { DefaultValues } from 'react-hook-form';
import { SchemaOf } from 'yup';

import { FormContext, useFormContext } from '../FormContext';
import { FormFields, FormFieldsSettings } from '../types';

type Props = {
  formFieldsSettings: FormFieldsSettings;
  defaultValues: DefaultValues<FormFields>;
  onSubmissionStateChange: OnSubmissionStateChange<FormFields>;
  onSubmit: (data: FormFields) => void;
  isSaving: boolean;
  isLoading: boolean;
  schema?: SchemaOf<FormFields>;
};
export const CollaboratorDrawerBody = (props: Props) => {
  const form = useMelioForm<FormFields>({
    onSubmit: props.onSubmit,
    schema: props.schema,
    mode: 'onSubmit',
    isSaving: props.isSaving,
    isLoading: props.isLoading,
    defaultValues: props.defaultValues,
    onSubmissionStateChange: props.onSubmissionStateChange,
  });

  return (
    <FormContext.Provider value={form}>
      <Form {...form.formProps}>
        <Group variant="vertical" spacing="xl">
          <Group.Item>
            <DetailsSection formFieldsSettings={pick(props.formFieldsSettings, ['firstName', 'lastName', 'email'])} />
          </Group.Item>
        </Group>
      </Form>
    </FormContext.Provider>
  );
};

const DetailsSection = ({
  formFieldsSettings,
}: {
  formFieldsSettings: Pick<FormFieldsSettings, 'firstName' | 'lastName' | 'email'>;
}) => {
  const { formatMessage } = useMelioIntl();
  const { registerField } = useFormContext();

  const firstNameField = registerField('firstName');
  const lastNameField = registerField('lastName');
  const emailField = registerField('email');

  return (
    <Card variant="flat" paddingX="l" paddingY="l">
      <Group variant="vertical" spacing="m" width="full">
        <Group.Item>
          <Text textStyle="heading2Semi">
            {formatMessage('activities.collaboratorDrawer.body.detailsSection.heading')}
          </Text>
        </Group.Item>

        <Group.Item>
          <Group variant="vertical" spacing="m">
            <Group.Item>
              <Form columns={2} as="div">
                <Form.ContentBox colSpan={1}>
                  <Form.TextField
                    {...firstNameField}
                    labelProps={{
                      label: formatMessage('activities.collaboratorDrawer.body.detailsSection.fields.firstName'),
                    }}
                    data-testid="collaborator-drawer-body-details-section-first-name-input"
                    isRequired={formFieldsSettings.firstName.required}
                    isViewMode={formFieldsSettings.firstName.readonly}
                  />
                </Form.ContentBox>
                <Form.ContentBox colSpan={1}>
                  <Form.TextField
                    {...lastNameField}
                    labelProps={{
                      label: formatMessage('activities.collaboratorDrawer.body.detailsSection.fields.lastName'),
                    }}
                    data-testid="collaborator-drawer-body-details-section-last-name-input"
                    isRequired={formFieldsSettings.lastName.required}
                    isViewMode={formFieldsSettings.lastName.readonly}
                  />
                </Form.ContentBox>
              </Form>
            </Group.Item>

            <Group.Item>
              <Form columns={1} as="div">
                <Form.ContentBox colSpan={1}>
                  <Form.TextField
                    type="email"
                    {...emailField}
                    labelProps={{
                      label: formatMessage('activities.collaboratorDrawer.body.detailsSection.fields.email'),
                    }}
                    data-testid="collaborator-drawer-body-details-section-email-input"
                    isRequired={formFieldsSettings.email.required}
                    isViewMode={formFieldsSettings.email.readonly}
                  />
                </Form.ContentBox>
              </Form>
            </Group.Item>
          </Group>
        </Group.Item>
      </Group>
    </Card>
  );
};
